import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Globe, Sparkles, Eye, Pencil, Copy, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, fmtDate, usePolling } from "@/lib/api";
import { PageHeader, EmptyState, Spinner, Tag, Alert } from "@/components/common";


const PageRow = ({ p, onChange, base }) => {
  const publicUrl = (slug) => `${base}/lp/${slug}`;
  const update = async (body, msg) => {
    try { await api.put(`/landing-pages/${p.id}`, body); toast.success(msg); onChange(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const copy = () => { navigator.clipboard?.writeText(publicUrl(p.slug)); toast.success("Public URL copied"); };
  return (
    <div data-testid={`lp-row-${p.slug}`} className="cc-card am-rise flex flex-col gap-4 p-5 lg:flex-row lg:items-center lg:justify-between">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-display text-xl font-bold uppercase tracking-tight text-slate-100">{p.title}</span>
          {p.status === "generating" && <Tag tone="cyan"><Spinner className="h-3 w-3" /> Generating</Tag>}
          {p.status === "error" && <Tag tone="red">Error</Tag>}
          {p.status === "ready" && <Tag tone={p.published ? "emerald" : "muted"} testid={`lp-published-${p.slug}`}>{p.published ? "Published" : "Draft"}</Tag>}
          {p.owner_approved ? <Tag tone="emerald" testid={`lp-approved-${p.slug}`}><BadgeCheck className="h-3 w-3" /> Approved by owner</Tag> : p.status === "ready" && <Tag tone="amber" testid={`lp-approved-${p.slug}`}>Awaiting owner review</Tag>}
        </div>
        <div className="mt-1 truncate font-mono text-xs text-slate-500">/lp/{p.slug} · {p.content?.headline || p.error || "…"} · {fmtDate(p.updated_at || p.created_at)}</div>
      </div>
      {p.status === "ready" && (
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-400"><Switch data-testid={`lp-publish-switch-${p.slug}`} checked={p.published} onCheckedChange={(v) => update({ published: v }, v ? "Published" : "Unpublished")} /> Live</label>
          <button data-testid={`lp-copy-${p.slug}`} onClick={copy} className="cc-btn cc-btn-ghost px-3 py-1.5 text-xs"><Copy className="h-3.5 w-3.5" /> URL</button>
          <Link data-testid={`lp-edit-${p.slug}`} to={`/landing-pages/${p.id}`} className="cc-btn cc-btn-ghost px-3 py-1.5 text-xs"><Pencil className="h-3.5 w-3.5" /> Preview & edit</Link>
          {p.published && <a data-testid={`lp-open-${p.slug}`} href={`/lp/${p.slug}`} target="_blank" rel="noreferrer" className="cc-btn cc-btn-ghost px-3 py-1.5 text-xs"><Eye className="h-3.5 w-3.5" /> Open live</a>}
          <button data-testid={`lp-approve-${p.slug}`} onClick={() => update({ owner_approved: !p.owner_approved }, p.owner_approved ? "Approval withdrawn" : "Marked approved by owner")} className={`cc-btn px-3 py-1.5 text-xs ${p.owner_approved ? "cc-btn-ghost" : "cc-btn-primary"}`}>
            <BadgeCheck className="h-3.5 w-3.5" /> {p.owner_approved ? "Withdraw approval" : "Approve page"}
          </button>
        </div>
      )}
    </div>
  );
};

export default function LandingPages() {
  const [pages, setPages] = useState(null);
  const [busy, setBusy] = useState(false);
  const [base, setBase] = useState(window.location.origin);
  useEffect(() => { api.get("/settings/app").then((r) => setBase(r.data.base)).catch(() => {}); }, []);
  const load = useCallback(() => api.get("/landing-pages").then((r) => setPages(r.data.pages)), []);
  useEffect(() => { load(); }, [load]);
  usePolling(load, Boolean(pages?.some((p) => p.status === "generating")), 4000);

  const generate = async () => {
    setBusy(true);
    try { await api.post("/landing-pages/generate", {}); toast.message("Claude is writing one page per campaign theme…"); load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="landing-pages-page">
      <PageHeader eyebrow="Lead capture" title="Landing Pages"
        description="One fast, mobile-first page per campaign theme, built from therehablabsg.com: real logo, colours, photos, prices and address. Campaigns can't go live until you mark their page “Approved by owner”."
        actions={<button data-testid="lp-generate-button" onClick={generate} disabled={busy} className="cc-btn cc-btn-primary">{busy ? <Spinner className="h-4 w-4 text-[#03130c]" /> : <Sparkles className="h-4 w-4" />} Generate from latest plan</button>} />
      <div className="mb-6"><Alert tone="cyan" title="Launch gate">Nothing is pushed to Google Ads until each page is reviewed and approved by you. Editing a page's text withdraws its approval.</Alert></div>
      {!pages ? <Spinner /> : pages.length === 0 ? (
        <EmptyState testid="lp-empty-state" icon={Globe} title="No landing pages yet" description="Generate pages from your latest Campaign Plan. Each page gets a headline matching its keywords, your real offer, a lead form and a sticky WhatsApp button." />
      ) : <div className="space-y-3">{pages.map((p) => <PageRow key={p.id} p={p} onChange={load} base={base} />)}</div>}
    </div>
  );
}
